import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWakeLock } from 'react-screen-wake-lock';
import { CalibrationStatusData } from 'lavva.exalushome.extalife/build/js/Devices/DeviceServices/FacadeBlindService/ZfBidiConfigParams';
import { Status } from 'lavva.exalushome/build/js/DataFrame';
import { CustomBackdrop, Header, NavHeader, Page, SubmitButton } from '../../../../../../../components';
import { usePreventBack } from '../../../../../../../hooks/router/use-navigation-guard';
import { ROUTES } from '../../../../../../../routes/routes';
import { toastInfo } from '../../../../../../../utils/toast';
import { ChannelBoxIcon } from '../../../../../components/channel/box-icon';
import { useHandleDataFrameErrors } from '../../../../../hooks/errors';
import { useConfigurationService } from '../../../../device-details/hooks/use-configuration-service';
import { useCalibration } from '../../common/hooks/use-calibration';
import './index.scss';

type ZfBidiCalibrationStep2Props = {
  setStep: Dispatch<SetStateAction<number>>;
};

const ZfBidiCalibrationStep2: React.FC<ZfBidiCalibrationStep2Props> = ({ setStep }) => {
  const { t: td } = useTranslation('device-add');
  const { channel, navigate, t, turnOnBackdrop, turnOffBackdrop, getZfBidiConfigService } = useCalibration();
  const { handleError } = useHandleDataFrameErrors();
  const [loading, setLoading] = useState<boolean>(false);
  const [calibrationSuccess, showCalibrationSuccess] = useState<boolean>(false);
  const { handleGetFacadeParamsResponse } = useConfigurationService();
  const { request, release } = useWakeLock();
  usePreventBack(loading, t('exalus.params.ZfBidiParams.calibrationFlow.noBackMessage'));

  useEffect(() => {
    request();

    return () => {
      release();
    };
  }, []);

  const handleClick = () => {
    if (channel) {
      setStep(3);
      navigate(`${ROUTES.FacadeCalibration(channel.ChannelId)}?step=3`);
    }
  };

  const handleStart = async () => {
    if (channel) {
      setLoading(true);
      const configService = await getZfBidiConfigService();
      const result = await configService.RunAutomaticLamelaPositionCalibrationAsync(
        channel?.GetDevice(),
        channel.Number,
      );
      handleGetFacadeParamsResponse<CalibrationStatusData>(
        result,
        () => {
          if (
            (result as CalibrationStatusData).IsCurrentPositionKnown &&
            (result as CalibrationStatusData).LamellaTiltCalibration &&
            (result as CalibrationStatusData).TravelTimeCalibration
          ) {
            showCalibrationSuccess(true);
          }
          setLoading(false);
        },
        () => {
          setLoading(false);
        },
      );
    }
  };

  const handleStop = async () => {
    if (channel) {
      turnOnBackdrop();
      const configService = await getZfBidiConfigService();
      const result = await configService.StopCalibrationAsync(channel?.GetDevice(), channel.Number);

      if (result === Status.OK) {
        setLoading(false);
        toastInfo({ content: t('exalus.params.ZfBidiParams.calibrationFlow.autoCalibrationStop') });
      } else {
        handleError(result);
      }

      turnOffBackdrop();
    }
  };

  return (
    <Page
      className="calibration-flow"
      isStickyHeader
      whiteContent
      header={
        <>
          <NavHeader />
          <Header title={t('exalus.calibration')} />
        </>
      }
    >
      {channel && <ChannelBoxIcon channel={channel} className="facade-icon" />}
      <p className="info m-t-40 m-b-40">{t('exalus.params.ZfBidiParams.calibrationFlow.startAutoCalibrate')}</p>
      <button className="button button--secondary m-t-24" onClick={handleStart} disabled={loading}>
        {t('exalus.params.ZfBidiParams.calibrationFlow.start')}
      </button>
      {calibrationSuccess && (
        <p className="success-message">{t('exalus.params.ZfBidiParams.calibrationFlow.autoCalibrationSuccess')}</p>
      )}
      <SubmitButton type="button" onClick={handleClick} tranparent disabled={loading}>
        {t('exalus.params.ZfBidiParams.calibrationFlow.next')}
      </SubmitButton>
      {loading && <CustomBackdrop loading btnTitle={td('exalus.searchStop')} onClick={handleStop} />}
    </Page>
  );
};

export default ZfBidiCalibrationStep2;
